import React from 'react';
import styled from 'styled-components';
import { IContact } from '@bb/contacts';
import { capitalize, formatLastName } from 'utils';
import { AuthContext, ContactDrawerContext } from 'contexts';
import {
  isUserLicensed,
  UserLicenseType,
  UserRoles,
  userRolesToLabelMap,
} from '@bb/users';
import { AnsweringSubscriptionTag } from '@bb/domain/dist/types';
import { SupraCoopType } from '@bb/domain/dist/types/SupraSystemSubscription';
import { SecondaryDescriptionTextStyle } from '@brokerbay/design-components';
import { MlsNumber } from '@bb/automaton';
import { Text } from '../Typography';
import Skeleton from '../Skeleton';
import Avatar from '../Avatar';
import Tooltip from '../Tooltip';
import { ALL_CONTACT_TAGS } from '../ContactTypeTags';
import theme from '../theme';
import {
  ContactDrawerMode,
  ContactDrawerType,
} from '../../Router/ContactDrawer/types';
import SupraCoopTypeTag from '../../Router/pages/MLSStaffPages/Tags/CoopTypeTag';

export type ContactType = {
  id?: string;
  avatarFilestackId?: string;
  firstName: string;
  lastName?: string;
  brokerageName?: string;
  location?: string;
  role?: string;
  title?: string;
  licenseType?: UserLicenseType;
  owner?: IContact['owner'];
  tags?: IContact['tags'];
  mls_numbers?: MlsNumber[];
  orgName?: string;
  branchName?: string;
  coopType?: SupraCoopType;
};

export type ContactProps = {
  contact?: ContactType;
  hoverable?: boolean;
  loading?: boolean;
  showBranchName?: boolean;
  showBrokerageName?: boolean;
  avatarSize?: number;
  onClick?: (id: string) => void;
  showContactDetails?: boolean;
  showTitle?: boolean;
  subscriptionTag?: AnsweringSubscriptionTag;
  type?: ContactDrawerType;
  secondaryText?: React.ReactNode;
  underlineOnHover?: boolean;
  viewNameOnToolTip?: boolean;
  disableClick?: boolean;
  isMobile?: boolean;
  truncateLastName?: boolean; // eg. 'John D'
  tooltipPlacement?: 'top' | 'bottom' | 'left' | 'right';
  additionalContacts?: ContactType[];
  showMlsIds?: boolean;
  showCoopType?: boolean;
};

const TRUNCATE_SIZE = 30;

export const Contact: React.FC<ContactProps> = ({
  contact,
  showBranchName,
  showBrokerageName = false,
  avatarSize,
  loading,
  hoverable = true,
  disableClick,
  onClick,
  showContactDetails,
  showTitle,
  type = ContactDrawerType.User,
  secondaryText,
  underlineOnHover = false,
  viewNameOnToolTip = false,
  isMobile,
  truncateLastName = false,
  tooltipPlacement = 'bottom',
  additionalContacts,
  subscriptionTag,
  showMlsIds,
  showCoopType,
}) => {
  const contactDrawerContext = React.useContext(ContactDrawerContext);
  const { auth } = React.useContext(AuthContext);
  const isMlsUser = auth?.org.type === 'mlsStaff';
  const _avatarSize = avatarSize || (isMobile ? 36 : 22);
  const horizontalGapSize = isMobile ? 12 : _avatarSize / 4;

  const handleClick = () => {
    if (disableClick) return;
    const { id } = contact;

    if (onClick) onClick(id);
    else if (contactDrawerContext) {
      contactDrawerContext.openContactDrawer(
        ContactDrawerMode.Profile,
        type,
        id,
        type === ContactDrawerType.Coop
          ? ({ contact, subscriptionTag } as any)
          : { subscriptionTag, ...(!auth && { agent: contact }) },
      );
    }
  };

  if (loading || !contact) {
    return (
      <StyledWrapper>
        <Skeleton
          className="skeleton"
          title={false}
          paragraph={{ rows: 1, width: '100%' }}
          avatar={{ size: 'small' }}
        />
      </StyledWrapper>
    );
  }

  const formatName = ({ firstName, lastName }: ContactType) =>
    `${capitalize(firstName || '')} ${formatLastName(lastName || '')}`.trim();

  const buildToolTipTitle = () => {
    if (viewNameOnToolTip) {
      if (additionalContacts?.length) {
        return [contact, ...additionalContacts].map(formatName).join(', ');
      }
      return formatName(contact);
    }
    return 'View Details';
  };

  return (
    <StyledWrapper>
      <Tooltip
        title={buildToolTipTitle()}
        placement={tooltipPlacement}
        trigger={'hover'}
        mouseEnterDelay={0.5}
        overlayStyle={hoverable ? { maxWidth: '80%' } : { display: 'none' }}
      >
        <StyledButton
          onClick={handleClick}
          $horizontalGapSize={horizontalGapSize}
          $disableClick={disableClick}
        >
          <Avatar width={_avatarSize} user={contact} />
          <StyledTextContainer data-cy="coop-contact-text">
            <StyledName
              data-cy="coop-contact-name"
              ellipsis
              block
              $underlineOnHover={underlineOnHover}
            >
              {`${capitalize(contact.firstName || '')} ${formatLastName(
                contact.lastName && truncateLastName
                  ? contact.lastName[0]
                  : contact.lastName || '',
              )}`.trim()}
              {additionalContacts?.length && `, +${additionalContacts.length}`}
              {showMlsIds && Boolean(contact.mls_numbers?.length) && (
                <>
                  {' '}
                  <Text type="secondary" ellipsis>
                    {(() => {
                      const idsConcat = `(${contact.mls_numbers
                        ?.map(({ number, board }) => `${board}: ${number}`)
                        .join(', ')})`;

                      if (idsConcat.length <= TRUNCATE_SIZE) return idsConcat;
                      return `${idsConcat.substring(0, TRUNCATE_SIZE)}...`;
                    })()}
                  </Text>
                </>
              )}{' '}
              {showCoopType && contact.coopType && (
                <SupraCoopTypeTag coopType={contact.coopType} />
              )}
            </StyledName>
            {secondaryText}
            {showContactDetails && (
              <StyledContactDetails>
                {contact.tags
                  ?.map(tag => ALL_CONTACT_TAGS[tag].label)
                  .join(', ')}{' '}
                &bull;{' '}
                {`${contact.owner?.firstName} ${contact.owner?.lastName}'s Contact`}
              </StyledContactDetails>
            )}
            {showBranchName && (
              <StyledSecondaryDetails type="secondary" ellipsis block>
                {isMlsUser
                  ? contact.branchName
                  : contact.branchName
                    ? `${contact.orgName} - ${contact.branchName}`
                    : auth?.org?.locations?.find((l, i) => i === Number(contact.location))?.name}
              </StyledSecondaryDetails>
            )}
            {showTitle && (
              <StyledSecondaryDetails type="secondary" ellipsis block>
                {isUserLicensed(contact) && contact.title
                  ? contact.title
                  : userRolesToLabelMap[contact.role as UserRoles]}
              </StyledSecondaryDetails>
            )}
            {isMobile && (
              <StyledBrokerageName>{contact.brokerageName}</StyledBrokerageName>
            )}
          </StyledTextContainer>
          {showBrokerageName && (
            <StyledBrokerageName>{contact.brokerageName}</StyledBrokerageName>
          )}
        </StyledButton>
      </Tooltip>
    </StyledWrapper>
  );
};

const StyledWrapper = styled.span<{ hoverable?: boolean }>`
  > .skeleton {
    display: grid;
    grid-template-columns: auto 1fr;
    align-items: center;
    margin-bottom: 4px;
    ul {
      margin-bottom: 0;
    }
  }

  ${({ hoverable }) =>
    hoverable
      ? `&:hover {
    text-decoration: underline;
  }`
      : ''}
`;

const StyledButton = styled.button<{
  $horizontalGapSize: number;
  $disableClick: boolean;
}>`
  display: grid;
  grid-template-columns: auto auto;
  gap: 3px ${({ $horizontalGapSize }) => $horizontalGapSize}px;
  margin-bottom: 4px;
  align-items: center;
  cursor: ${({ $disableClick }) => ($disableClick ? 'default' : 'pointer')};
`;

const StyledBrokerageName = styled(Text)`
  ${SecondaryDescriptionTextStyle}
  grid-column: 1 / span 3;
`;

const StyledSecondaryDetails = styled(Text)`
  font-size: 13px;
  line-height: 1.2;
`;

const StyledTextContainer = styled.div<{ underlineOnHover?: boolean }>`
  display: flex;
  flex-direction: column;
  overflow: hidden;
  color: ${theme.gray9};
`;

const StyledName = styled(Text)<{ $underlineOnHover?: boolean }>`
  &:hover {
    text-decoration: ${({ $underlineOnHover }) =>
      $underlineOnHover && 'underline'};
  }
`;

const StyledContactDetails = styled.div`
  font-size: 12px;
  color: ${theme.gray7};
`;

export default Contact;
